import React from 'react';
import { FormControl, Box, FormLabel, FormHelperText, Select, InputGroup, Input, InputRightElement, Flex } from '@chakra-ui/react';
import { Field } from 'formik';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import c from '../../constant';

/* This is Yup schema. Operator and Currency need default value each. Reference in Revenue filter.
amount: Yup.object()
        .shape({
            operator: Yup.string(),
            amount: Yup.number()
                .typeError('required to input number')
                .positive('required to input number greater than zero')
                .integer('required to input only integer'),
            currency: Yup.string(),
        })
        .nullable(),
*/

const operatorOptions = [
	{ titleEn: 'Equal (=)', titleKh: 'ស្មើ (=)', operator: 'equal' },
	{ titleEn: 'Not Equal (!=)', titleKh: 'មិនស្មើ (!=)', operator: 'not-equal' },
	{ titleEn: 'Greater Than (>)', titleKh: 'ច្រើនជាង (>)', operator: 'greater-than' },
	{ titleEn: 'Less Than (<)', titleKh: 'តិចជាង (<)', operator: 'less-than' },
	{ titleEn: 'Greater Than or Equal (>=)', titleKh: 'ច្រើនជាង ឬស្មើ (>=)', operator: 'greater-than-equal' },
	{ titleEn: 'Less Than or Equal (<=)', titleKh: 'តិចជាង ឬស្មើ (<=)', operator: 'less-than-equal' },
];

const operatorSignMapping = {
	equal: { titleEn: 'Equal (=)', titleKh: 'ស្មើ (=)' },
	'not-equal': { titleEn: 'Not Equal (!=)', titleKh: 'មិនស្មើ (!=)' },
	'greater-than': { titleEn: 'Greater Than (>)', titleKh: 'ច្រើនជាង (>)' },
	'less-than': { titleEn: 'Less Than (<)', titleKh: 'តិចជាង (<)' },
	'greater-than-equal': { titleEn: 'Greater Than or Equal (>=)', titleKh: 'ច្រើនជាង ឬស្មើ (>=)' },
	'less-than-equal': { titleEn: 'Less Than or Equal (<=)', titleKh: 'តិចជាង ឬស្មើ (<=)' },
};

const SearchAmountFormField = ({
	lang = 'kh',
	name = null,
	label = null,
	disabled = false,
	required = false,
	decimalScale = 2,
	allowNegative = false,
	spacing = 4,
}) => {
	const { t } = useTranslation('common');

	return (
		<Field name={name}>
			{({ field, meta, form }) => {
				return (
					<Box mb="4" overflow="hidden">
						<FormControl id={name}>
							{label && (
								<FormLabel>
									{t(label)}
									{required && <span style={{ color: 'red' }}>*</span>}
								</FormLabel>
							)}
							<Flex>
								<Flex flex={2} mr={spacing}>
									{/**
									 * Supposely it always has value.
									 * Do not add placeholder.
									 * Default equal
									 * */}
									<Select
										h={12}
										value={field?.value?.operator}
										onChange={(e) => form.setFieldValue(name, { ...field?.value, operator: e?.target?.value })}
										borderRadius={c.borderRadius}
										disabled={disabled}
									>
										{operatorOptions?.map((item, index) => (
											<option key={`option-item-${index}`} value={item?.operator}>
												{lang === 'kh' ? t(item?.titleKh) : t(item?.titleEn)}
											</option>
										))}
									</Select>
								</Flex>
								<Flex flex={4}>
									<InputGroup size="md">
										<NumericFormat
											{...field}
											customInput={Input}
											fontSize="lg"
											name="name"
											value={field?.value?.amount}
											placeholder={t('Amount')}
											thousandSeparator={true}
											decimalScale={decimalScale}
											allowNegative={allowNegative}
											onValueChange={(values) => form?.setFieldValue(name, { ...field?.value, amount: values?.floatValue })}
											disabled={disabled}
											width="100%"
											height="49px"
											style={{ borderWidth: '1px', borderColor: '#E2E8F0' }}
										/>
										<InputRightElement width="6rem">
											{/**
											 * Supposely it always has value.
											 * Do not add placeholder.
											 * Default KHR
											 * */}
											<Select
												variant="filled"
												defaultValue={field?.value?.currency}
												onChange={(e) => form?.setFieldValue(name, { ...field?.value, currency: e?.target?.value })}
												fontWeight={'bold'}
												fontSize={'lg'}
												m={'10px 5px 0 0'}
												p={'0'}
												cursor={'pointer'}
											>
												<option value="KHR">KHR</option>
												<option value="USD">USD</option>
											</Select>
										</InputRightElement>
									</InputGroup>
								</Flex>
							</Flex>
							{meta?.touched && meta?.error && (
								<FormHelperText id="error-message-password" color="red.400" fontSize="12px">
									{meta?.error?.amount && `${t('Amount')} ${t('is')} ${t(meta?.error?.amount)}`}
								</FormHelperText>
							)}
						</FormControl>
					</Box>
				);
			}}
		</Field>
	);
};

export { SearchAmountFormField, operatorOptions, operatorSignMapping };
